/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';

import {
   formatCurrency,
   formatPercent,
   pluralTitulos,
   DASH,
} from '../utils/format';
import {
   Tile,
   TileGrid,
   TileLabel,
   TileSub,
   TileValue,
} from '../styles';
import { agingShape, blockShape, summaryShape } from '../utils/shapes';
import { SkeletonBar } from '../../../../styles/feedback/Skeleton';

// ---------------------------------------------------------------------------
// Dobra 1 · Tiles da saúde da carteira.
//
// Cinco números de topo, vindos de DOIS endpoints: emitido/recebido/redução do
// `summary`, carteira e vencido do `aging`. Como os dois carregam em paralelo e
// independentes, cada tile mostra seu próprio skeleton — os do summary não
// esperam o aging.
//
// Regra da contagem como fonte da verdade (0084 §5.8): com `titulos === 0` o
// tile NÃO imprime moeda. "R$ 0,00" ao lado de "0 títulos" tem cara de dado
// medido; o traço diz a mesma coisa sem fingir precisão. O zero continua dito,
// explicitamente, na linha de baixo — não é dado faltante, é zero real.
// ---------------------------------------------------------------------------

function TileBody({ block, value, sub, danger }) {
   if (block.loading && !block.data) {
      return (
         <>
            <SkeletonBar $h={30} $w="70%" />
            <SkeletonBar $h={12} $w="90%" />
         </>
      );
   }
   if (block.error) {
      return (
         <>
            <TileValue>{DASH}</TileValue>
            <TileSub>Indisponível</TileSub>
         </>
      );
   }
   return (
      <>
         <TileValue $danger={danger}>{value}</TileValue>
         <TileSub>{sub}</TileSub>
      </>
   );
}

TileBody.propTypes = {
   block: PropTypes.shape({
      loading: PropTypes.bool,
      data: PropTypes.oneOfType([summaryShape, agingShape]),
      error: PropTypes.shape({}),
   }).isRequired,
   value: PropTypes.node,
   sub: PropTypes.node,
   danger: PropTypes.bool,
};

// Moeda só quando há título por trás dela.
function moneyWithCount(count, amount) {
   return count ? formatCurrency(amount) : DASH;
}

export default function PortfolioTiles({ summary, aging }) {
   const s = summary.data;
   const a = aging.data;

   const emitidoCount = s?.emitido?.titulos ?? 0;
   const recebidoCount = s?.recebido?.titulos ?? 0;
   const abertoCount = a?.total_em_aberto?.titulos ?? 0;
   const vencidoCount = a?.vencido?.titulos ?? 0;

   return (
      <TileGrid>
         <Tile>
            <TileLabel>Emitido no período</TileLabel>
            <TileBody
               block={summary}
               value={moneyWithCount(emitidoCount, s?.emitido?.valor)}
               sub={
                  emitidoCount
                     ? `${pluralTitulos(emitidoCount)} · ticket ${formatCurrency(s?.ticket_medio)}`
                     : 'Nenhum título emitido no recorte'
               }
            />
         </Tile>

         <Tile>
            <TileLabel>Recebido</TileLabel>
            <TileBody
               block={summary}
               value={moneyWithCount(recebidoCount, s?.recebido?.valor)}
               sub={
                  recebidoCount
                     ? `${pluralTitulos(recebidoCount)} liquidados`
                     : 'Nenhum título liquidado'
               }
            />
         </Tile>

         <Tile>
            <TileLabel>Carteira em aberto</TileLabel>
            <TileBody
               block={aging}
               value={moneyWithCount(abertoCount, a?.total_em_aberto?.valor)}
               sub={
                  abertoCount
                     ? `${pluralTitulos(abertoCount)} · a vencer ${formatCurrency(a?.a_vencer?.valor)}`
                     : 'Nada em aberto'
               }
            />
         </Tile>

         <Tile>
            <TileLabel>Vencido</TileLabel>
            <TileBody
               block={aging}
               danger={vencidoCount > 0}
               value={moneyWithCount(vencidoCount, a?.vencido?.valor)}
               sub={
                  vencidoCount
                     ? `${pluralTitulos(vencidoCount)} · ${formatPercent(a?.vencido?.share_da_carteira_pct, 2)} da carteira`
                     : 'Nada vencido no recorte'
               }
            />
         </Tile>

      </TileGrid>
   );
}

PortfolioTiles.propTypes = {
   summary: blockShape(summaryShape).isRequired,
   aging: blockShape(agingShape).isRequired,
};
